<template>
  <main class="pc-event-preorder-page">
    <div v-if="!pageLoading && pageError" class="page-state" role="alert">
      <van-icon name="warning-o" size="46" />
      <h1>Event could not be loaded</h1>
      <p>{{ pageError }}</p>
      <van-button type="primary" class="state-button" @click="loadPage()">Retry</van-button>
    </div>

    <div v-else-if="!pageLoading && pageData && !pageData.activity.isVisible" class="page-state">
      <van-icon name="clock-o" size="52" />
      <h1>{{ pageData.unavailableMessage || 'The reservation activity has ended.' }}</h1>
      <NuxtLink to="/" class="text-action">Back to Kilimall</NuxtLink>
    </div>

    <template v-else-if="!pageLoading && pageData">
      <header class="event-header">
        <div class="header-inner">
          <NuxtLink to="/" class="brand" aria-label="Kilimall home">
            <img class="brand-logo" src="https://img.kilimall.com/c/h5/logo_icon_164_x3.png" alt="Kilimall">
          </NuxtLink>
          <div class="event-heading">
            <div class="event-title-row">
              <h1>{{ pageData.activity.name }}</h1>
              <span :class="['status-badge', pageData.activity.status.toLowerCase()]" role="status">
                {{ activityStatusLabel }}
              </span>
            </div>
            <p>{{ activityTimeText }}</p>
          </div>
          <details v-if="accountId" ref="accountMenu" class="account-menu">
            <summary class="account-button">
              <span class="account-avatar" aria-hidden="true">{{ accountInitial }}</span>
              <span class="account-name">{{ accountName }}</span>
              <span class="account-caret" aria-hidden="true"></span>
            </summary>
            <div class="account-links">
              <button type="button" @click="signOut">Sign out</button>
            </div>
          </details>
          <button v-else type="button" class="account-button sign-in-button" @click="requestLogin()">
            <van-icon name="contact-o" size="22" />
            Sign in
          </button>
        </div>
      </header>

      <div class="event-content">
        <section class="video-section section-panel" aria-label="Event live stream">
          <EventPreorderYoutubeEmbed :embed-code="pageData.activity.liveEmbedCode" />
        </section>

        <section ref="productsSection" class="products-section section-panel" aria-label="Pre-launch products">
          <div v-if="products.length === 0" class="content-empty">
            <van-icon name="bag-o" size="42" />
            <strong>No pre-launch products yet</strong>
            <span>Please stay tuned.</span>
          </div>
          <div v-else class="product-grid">
            <article v-for="product in products" :key="product.id" class="product-card">
              <div class="product-image-wrap">
                <img
                  v-if="!failedProductImages.has(product.id)"
                  class="product-image"
                  :src="product.imageUrl"
                  :alt="product.name"
                  @error="markProductImageFailed(product.id)"
                >
                <div v-else class="product-image fallback-image" aria-hidden="true">
                  <van-icon name="photo-o" size="48" />
                </div>
              </div>
              <div class="product-info">
                <div class="product-title-row">
                  <h2>{{ product.name }}</h2>
                  <button type="button" class="details-button" @click="openProductDetails(product)">Details</button>
                </div>
                <p class="estimated-price">Est. {{ priceRange(product) }}</p>
                <div class="product-tags">
                  <span :class="['reservation-tag', { closed: product.reservationClosed }]">
                    {{ product.reservationClosed ? 'Reservation closed' : 'Reservation open' }}
                  </span>
                  <span v-if="product.reservationCountDisplay" class="reservation-count">
                    <van-icon name="star-o" />
                    {{ product.reservationCountDisplay }} people reserved
                  </span>
                </div>
                <button
                  type="button"
                  class="reserve-button"
                  :disabled="product.isReserved || product.reservationClosed"
                  @click="openReservation(product)"
                >
                  {{ reserveButtonText(product) }}
                </button>
              </div>
            </article>
          </div>
        </section>

        <section class="reservations-section section-panel" aria-labelledby="pc-reservations-heading">
          <div class="reservations-heading">
            <h2 id="pc-reservations-heading">
              My Reservations <span class="reservation-total">{{ myReservations.length }}</span>
            </h2>
            <p>When the sale starts, select Buy Now, choose an available product, and order on the product page.</p>
          </div>
          <div v-if="!accountId" class="content-empty">
            <van-icon name="contact-o" size="42" />
            <strong>Sign in to view your reservations</strong>
            <span>Your active reservations will appear here.</span>
            <van-button type="primary" class="state-button" @click="requestLogin()">Sign in</van-button>
          </div>
          <div v-else-if="reservationsLoading" class="content-empty" role="status">
            <van-loading size="28" />
            <span>Loading your reservations...</span>
          </div>
          <div v-else-if="myReservations.length === 0" class="content-empty">
            <van-icon name="orders-o" size="42" />
            <strong>No active reservations</strong>
            <span>Reserve a product from the Pre-launch section.</span>
            <button type="button" class="text-action" @click="browseProducts">Browse products</button>
          </div>
          <div v-else class="reservation-list">
            <article v-for="reservation in myReservations" :key="reservation.id" class="reservation-card">
              <img
                v-if="productForReservation(reservation)?.imageUrl && !failedProductImages.has(reservation.productId)"
                class="reservation-image"
                :src="productForReservation(reservation)?.imageUrl"
                :alt="reservation.productName"
                @error="markProductImageFailed(reservation.productId)"
              >
              <div v-else class="reservation-image fallback-image">
                <van-icon name="photo-o" size="26" />
              </div>
              <div class="reservation-info">
                <h3>{{ reservation.productName }}</h3>
                <p>{{ reservation.canBuy ? 'On sale now' : `On sale ${formatSaleTime(reservation.saleTime)}` }}</p>
                <span v-if="reservation.couponClaimStatus === 'CLAIMED'" class="coupon-claimed">
                  <van-icon name="gift-o" /> Coupon claimed
                </span>
              </div>
              <div class="reservation-actions">
                <button type="button" class="buy-button" :disabled="!reservation.canBuy" @click="openAvailableListings(reservation)">
                  {{ reservation.canBuy ? 'Buy Now' : 'Waiting for sale' }}
                </button>
                <button v-if="!reservation.canBuy" type="button" class="cancel-reservation" @click="cancelReservation(reservation)">
                  Cancel
                </button>
              </div>
            </article>
          </div>
        </section>
      </div>
    </template>

    <EventPreorderPcReservationPopup
      v-model:show="reservationPopupVisible"
      v-model:phone="reservationPhone"
      :product="selectedProduct"
      :submitting="reservationSubmitting"
      @confirm="confirmReservation"
    />
    <EventPreorderPcListingPopup
      v-model:show="listingPopupVisible"
      :product-name="listingProductName"
      :listings="availableListings"
      :loading="listingsLoading"
      :error-message="listingsError"
      @retry="loadAvailableListings"
      @select="selectListing"
    />
  </main>
  <PCFooter />
</template>

<script setup lang="ts">
import { showConfirmDialog, showToast } from 'vant'
import {
  cancelEventPreorderReservation,
  createEventPreorderReservation,
  getEventPreorderAvailableListings,
  getEventPreorderPage,
  getMyEventPreorderReservations,
  type EventPreorderAvailableListing,
  type EventPreorderPageData,
  type EventPreorderProduct,
  type EventPreorderReservation
} from '@/api/models/event-preorder'
import { normalizeEventPreorderPhone } from '@/utils/event-preorder-phone'
import { getListingDetailHref } from '@/plugins/utils'
import { logoutResetUserIdFn } from '@/plugins/bi-sa.client'

// PC retains the deployed WAP API contracts; the WAP implementation stays separate.

const route = useRoute()
const router = useRouter()
const userInfo = useUserInfo()
const userInfoCookie = useCookie<IUserInfo | null>('UserInfo')
const { $currencySymbol, $toThousandsFn, $toListingDetail } = useNuxtApp()

const pageSlug = computed(() => String(route.params.pageSlug || ''))
const accountId = computed<number | null>(() => {
  const value = Number(userInfo.value?.userId)
  return Number.isInteger(value) && value > 0 ? value : null
})
const boundPhone = computed(() => String(userInfo.value?.phone || '').trim())

const pageData = ref<EventPreorderPageData | null>(null)
const pageLoading = ref(true)
const eventPreorderLoading = useState<boolean>('event-preorder-pc-loading', () => false)
watch(pageLoading, (loading) => {
  if (import.meta.client) eventPreorderLoading.value = loading
}, { immediate: true })
const pageError = ref('')
const productsSection = ref<HTMLElement | null>(null)
const accountMenu = ref<HTMLDetailsElement | null>(null)
const accountName = computed(() => String(userInfo.value?.nick || '').trim() || 'My account')
const accountInitial = computed(() => accountName.value.charAt(0).toUpperCase())
const browseProducts = () => productsSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
const signOut = () => {
  if (!import.meta.client) return
  if (accountMenu.value) accountMenu.value.open = false
  sessionStorage.removeItem('event-preorder-pending-product')
  userInfoCookie.value = null
  userInfo.value = null
  logoutResetUserIdFn()
}
const closeAccountMenuOnOutsideClick = (event: PointerEvent) => {
  if (accountMenu.value?.open && !accountMenu.value.contains(event.target as Node)) {
    accountMenu.value.open = false
  }
}
const myReservations = ref<EventPreorderReservation[]>([])
const reservationsLoading = ref(false)
let pageRequestSequence = 0
let reservationsRequestSequence = 0
let stateRefreshTimer: ReturnType<typeof setTimeout> | null = null
let serverTimestampAtSync = 0
let localTimestampAtSync = 0
let pageMounted = false

const MAX_TIMEOUT_MS = 2_147_000_000
const STATE_REFRESH_BUFFER_MS = 300
const STATE_REFRESH_RETRY_MS = 15_000

const products = computed(() => pageData.value?.products || [])
const failedProductImages = ref(new Set<number>())
const activityStatusLabel = computed(() => pageData.value?.activity.status === 'ON_SALE' ? 'LIVE' : 'UPCOMING')
const activityTimeText = computed(() => pageData.value
  ? `${formatEventDate(pageData.value.activity.saleTime)} EAT`
  : '')

const eventDateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: 'Africa/Nairobi'
})

const getEventDateTimeParts = (value: string) =>
  eventDateTimeFormatter.formatToParts(new Date(value)).reduce<Record<string, string>>((result, part) => {
    if (part.type !== 'literal') result[part.type] = part.value
    return result
  }, {})

const formatEventDate = (value: string) => {
  if (!value) return ''
  try {
    const { weekday, day, month, hour, minute, dayPeriod } = getEventDateTimeParts(value)
    return `${weekday}, ${day} ${month}, ${hour}:${minute} ${dayPeriod.toUpperCase()}`
  } catch {
    return value
  }
}

const formatSaleTime = (value: string) => {
  if (!value) return ''
  try {
    const { month, day, hour, minute, dayPeriod } = getEventDateTimeParts(value)
    return `${month} ${day}, ${hour}:${minute} ${dayPeriod.toUpperCase()}`
  } catch {
    return value
  }
}

const normalizeServerTimestamp = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return 0
  return value < 1_000_000_000_000 ? value * 1000 : value
}

const syncServerClock = (timestamp: number) => {
  const normalizedTimestamp = normalizeServerTimestamp(Number(timestamp))
  if (!normalizedTimestamp) return
  serverTimestampAtSync = normalizedTimestamp
  localTimestampAtSync = Date.now()
}

const estimatedServerTimestamp = () => serverTimestampAtSync
  ? serverTimestampAtSync + (Date.now() - localTimestampAtSync)
  : 0

const clearStateRefreshTimer = () => {
  if (stateRefreshTimer !== null) {
    clearTimeout(stateRefreshTimer)
    stateRefreshTimer = null
  }
}

const stateBoundaryTimestamp = (value?: string | null) => {
  if (!value) return 0
  const timestamp = new Date(value).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

const refreshTimedState = async () => {
  const pageRefreshed = await loadPage(false, false)
  if (!pageMounted) return
  let reservationsRefreshed = true

  if (accountId.value) {
    reservationsRefreshed = await loadMyReservations(false, false)
  }

  if (pageRefreshed && reservationsRefreshed) {
    scheduleNextStateRefresh()
  } else {
    scheduleStateRefreshRetry()
  }
}

const scheduleStateRefreshRetry = () => {
  if (!import.meta.client || !pageMounted || !serverTimestampAtSync) return
  clearStateRefreshTimer()
  stateRefreshTimer = setTimeout(() => {
    stateRefreshTimer = null
    void refreshTimedState()
  }, STATE_REFRESH_RETRY_MS)
}

const scheduleNextStateRefresh = () => {
  if (!import.meta.client || !pageMounted) return
  clearStateRefreshTimer()

  const serverNow = estimatedServerTimestamp()
  const activity = pageData.value?.activity
  if (!serverNow || !activity) return

  const boundaries = [
    stateBoundaryTimestamp(activity.displayStartTime),
    stateBoundaryTimestamp(activity.saleTime),
    stateBoundaryTimestamp(activity.displayEndTime),
    ...myReservations.value.map((reservation) => stateBoundaryTimestamp(reservation.saleTime))
  ].filter((timestamp) => timestamp > serverNow)

  if (!boundaries.length) return
  const nextBoundary = boundaries.reduce((nearest, timestamp) => Math.min(nearest, timestamp))
  const delay = Math.min(
    Math.max(nextBoundary - serverNow + STATE_REFRESH_BUFFER_MS, 0),
    MAX_TIMEOUT_MS
  )

  stateRefreshTimer = setTimeout(() => {
    stateRefreshTimer = null
    if (estimatedServerTimestamp() < nextBoundary) {
      scheduleNextStateRefresh()
      return
    }
    void refreshTimedState()
  }, delay)
}

const getErrorMessage = (error: any, fallback: string) => error?.msg || error?.message || fallback

const markProductImageFailed = (productId: number) => {
  failedProductImages.value = new Set(failedProductImages.value).add(productId)
}

const restorePendingReservation = () => {
  if (!accountId.value || !import.meta.client) return
  const pendingProductId = Number(sessionStorage.getItem('event-preorder-pending-product'))
  if (!pendingProductId) return

  sessionStorage.removeItem('event-preorder-pending-product')
  const pendingProduct = products.value.find((item) => item.id === pendingProductId)
  if (pendingProduct && !pendingProduct.isReserved && !pendingProduct.reservationClosed) {
    openReservation(pendingProduct)
  }
}

const loadPage = async (showLoading = true, scheduleRefresh = true): Promise<boolean> => {
  const requestSequence = ++pageRequestSequence
  if (showLoading) pageLoading.value = true
  if (showLoading) pageError.value = ''

  try {
    const params: { region_code: string; account_id?: number } = {
      region_code: useCurrentCountryCode().value || 'KE'
    }
    if (accountId.value) params.account_id = accountId.value

    const response = await getEventPreorderPage(pageSlug.value, params)
    if (requestSequence !== pageRequestSequence) return false

    const data = response.data
    syncServerClock(response.timestamp)
    pageData.value = data
    myReservations.value = accountId.value ? data.myReservations || [] : []
    syncOpenPopups()
    if (scheduleRefresh) scheduleNextStateRefresh()
    nextTick(restorePendingReservation)
    return true
  } catch (error: any) {
    if (requestSequence !== pageRequestSequence) return false
    if (showLoading) {
      pageError.value = getErrorMessage(error, 'Please check your connection and try again.')
    } else if (scheduleRefresh) {
      scheduleStateRefreshRetry()
    }
    return false
  } finally {
    if (requestSequence === pageRequestSequence) pageLoading.value = false
  }
}

const loadMyReservations = async (showLoading = true, scheduleRefresh = true): Promise<boolean> => {
  const currentAccountId = accountId.value
  const currentActivityId = pageData.value?.activity.id
  if (!currentAccountId || !currentActivityId) return false

  const requestSequence = ++reservationsRequestSequence
  if (showLoading) reservationsLoading.value = true
  try {
    const response = await getMyEventPreorderReservations(
      currentActivityId,
      currentAccountId
    )
    if (requestSequence !== reservationsRequestSequence
      || accountId.value !== currentAccountId
      || pageData.value?.activity.id !== currentActivityId) return false

    syncServerClock(response.timestamp)
    myReservations.value = response.data
    syncOpenPopups()
    if (scheduleRefresh) scheduleNextStateRefresh()
    return true
  } catch {
    // The request layer displays the API error message.
    if (requestSequence === reservationsRequestSequence && !showLoading && scheduleRefresh) {
      scheduleStateRefreshRetry()
    }
    return false
  } finally {
    if (requestSequence === reservationsRequestSequence && showLoading) {
      reservationsLoading.value = false
    }
  }
}

const requestLogin = (pendingProductId?: number) => {
  if (!import.meta.client) return
  if (pendingProductId) {
    sessionStorage.setItem('event-preorder-pending-product', String(pendingProductId))
  }
  router.push(`/login?redirect=${route.fullPath}`)
}

const priceRange = (product: EventPreorderProduct) =>
  `${$currencySymbol()} ${$toThousandsFn(product.estimatedPriceMin)}-${$toThousandsFn(product.estimatedPriceMax)}`

const reserveButtonText = (product: EventPreorderProduct) => {
  if (product.reservationClosed) return 'Reservation closed'
  if (product.isReserved) return 'Reserved ✓'
  return 'Reserve Now'
}

const openProductDetails = (product: EventPreorderProduct) => {
  if (!import.meta.client || !product.detailUrl) return
  try {
    const url = new URL(product.detailUrl)
    if (!['http:', 'https:'].includes(url.protocol)) throw new Error('Invalid protocol')
    const detailTab = window.open('about:blank', '_blank')
    if (!detailTab) {
      window.location.href = url.toString()
      return
    }
    detailTab.opener = null
    detailTab.location.href = url.toString()
  } catch {
    showToast('Product details are currently unavailable.')
  }
}

const selectedProduct = ref<EventPreorderProduct | null>(null)
const reservationPopupVisible = ref(false)
const reservationPhone = ref('')
const reservationSubmitting = ref(false)

const openReservation = (product: EventPreorderProduct) => {
  if (product.isReserved || product.reservationClosed) return
  if (!accountId.value) {
    requestLogin(product.id)
    return
  }

  selectedProduct.value = product
  reservationPhone.value = boundPhone.value
  reservationPopupVisible.value = true
}

const confirmReservation = async () => {
  if (reservationSubmitting.value || !selectedProduct.value || !accountId.value) return
  const phone = normalizeEventPreorderPhone(
    reservationPhone.value,
    useCurrentCountryCode().value || 'KE'
  )
  if (!phone) {
    showToast('Enter a valid phone number.')
    return
  }

  reservationSubmitting.value = true
  try {
    const reservation = await createEventPreorderReservation(selectedProduct.value.id, {
      accountId: accountId.value,
      phone
    })

    reservationPopupVisible.value = false
    showToast(reservation.couponClaimStatus === 'CLAIMED'
      ? 'Reserved successfully. Coupon claimed.'
      : 'Reserved successfully. Coupon was not claimed.')
    await loadPage(false)
  } catch {
    // The request layer displays duplicate-reservation and validation messages.
  } finally {
    reservationSubmitting.value = false
  }
}

const productForReservation = (reservation: EventPreorderReservation) =>
  products.value.find((product) => product.id === reservation.productId)

const cancelReservation = async (reservation: EventPreorderReservation) => {
  if (!accountId.value) return
  try {
    await showConfirmDialog({
      title: 'Cancel reservation?',
      message: `${reservation.productName} will be removed from My Reservations. Any coupon already claimed will remain in your account.`,
      confirmButtonText: 'Cancel reservation',
      cancelButtonText: 'Keep it',
      confirmButtonColor: '#c91d2b'
    })
  } catch {
    return
  }

  try {
    await cancelEventPreorderReservation(reservation.id, accountId.value)
    showToast('Reservation cancelled.')
    await loadPage(false)
    await loadMyReservations()
  } catch {
    // The request layer displays the cancellation error.
  }
}

const listingPopupVisible = ref(false)
const listingReservation = ref<EventPreorderReservation | null>(null)
const listingProductName = ref('')
const availableListings = ref<EventPreorderAvailableListing[]>([])
const listingsLoading = ref(false)
const listingsError = ref('')
let listingRequestSequence = 0

const openAvailableListings = (reservation: EventPreorderReservation) => {
  if (!reservation.canBuy || !accountId.value) return
  listingReservation.value = reservation
  listingProductName.value = reservation.productName
  availableListings.value = []
  listingsError.value = ''
  listingPopupVisible.value = true
  loadAvailableListings()
}

const loadAvailableListings = async () => {
  const reservation = listingReservation.value
  const currentAccountId = accountId.value
  if (!reservation || !currentAccountId) return

  const requestSequence = ++listingRequestSequence
  const productId = reservation.productId
  const isCurrentRequest = () => requestSequence === listingRequestSequence
    && listingPopupVisible.value
    && listingReservation.value?.productId === productId
    && accountId.value === currentAccountId

  listingsLoading.value = true
  listingsError.value = ''
  try {
    const listings = await getEventPreorderAvailableListings(
      productId,
      currentAccountId
    )
    if (!isCurrentRequest()) return
    availableListings.value = listings
  } catch (error: any) {
    if (!isCurrentRequest()) return
    listingsError.value = getErrorMessage(error, 'Please try again.')
  } finally {
    if (requestSequence === listingRequestSequence) listingsLoading.value = false
  }
}

const openListingDetail = async (listingId: number) => {
  if (!import.meta.client) return
  const detailTab = window.open('about:blank', '_blank')
  if (!detailTab) {
    $toListingDetail(listingId)
    return
  }
  detailTab.opener = null
  const { url, cacheWrite } = getListingDetailHref(listingId)
  await cacheWrite
  detailTab.location.href = url
}

const selectListing = (listing: EventPreorderAvailableListing) => {
  if (!listing.selectable || listing.stockStatus === 'SOLD_OUT') return
  listingPopupVisible.value = false
  void openListingDetail(listing.listingId)
}

const syncOpenPopups = () => {
  if (!pageData.value?.activity.isVisible) {
    reservationPopupVisible.value = false
    listingPopupVisible.value = false
    selectedProduct.value = null
    return
  }

  if (reservationPopupVisible.value) {
    const product = products.value.find((item) => item.id === selectedProduct.value?.id)
    if (!product || product.reservationClosed || product.isReserved) {
      reservationPopupVisible.value = false
      selectedProduct.value = null
    } else {
      selectedProduct.value = product
    }
  }

  if (listingPopupVisible.value) {
    const reservation = myReservations.value.find((item) => item.id === listingReservation.value?.id)
    if (!reservation?.canBuy) {
      listingPopupVisible.value = false
    } else {
      listingReservation.value = reservation
    }
  }
}

watch(listingPopupVisible, (visible) => {
  if (!visible) {
    listingRequestSequence++
    listingsLoading.value = false
  }
})

onMounted(() => {
  pageMounted = true
  document.addEventListener('pointerdown', closeAccountMenuOnOutsideClick)
  void loadPage()
})

onBeforeUnmount(() => {
  pageMounted = false
  document.removeEventListener('pointerdown', closeAccountMenuOnOutsideClick)
  eventPreorderLoading.value = false
  clearStateRefreshTimer()
  pageRequestSequence++
  reservationsRequestSequence++
  listingRequestSequence++
})

watch(accountId, (value, oldValue) => {
  if (value !== oldValue) {
    clearStateRefreshTimer()
    reservationsRequestSequence++
    listingRequestSequence++
    listingPopupVisible.value = false
    reservationPopupVisible.value = false
    selectedProduct.value = null
    myReservations.value = []
    reservationsLoading.value = false
    pageData.value = null
    loadPage()
  }
})

// Nuxt can reuse the route component when navigating to another event.
watch(pageSlug, () => {
  clearStateRefreshTimer()
  reservationsRequestSequence++
  listingRequestSequence++
  pageData.value = null
  myReservations.value = []
  failedProductImages.value = new Set()
  reservationPopupVisible.value = false
  listingPopupVisible.value = false
  selectedProduct.value = null
  reservationsLoading.value = false
  serverTimestampAtSync = 0
  void loadPage()
})
</script>

<style scoped lang="scss">
// PC uses 2x design pixels: PostCSS converts at 72px/rem, desktop root is 36px.
.pc-event-preorder-page {
  min-height: 100vh;
  color: #252525;
  background: #f5f6f8;
  font-size: 32px;
  line-height: 1.4;

  button, summary { cursor: pointer; }
  button { font-family: inherit; }
  button:disabled { cursor: not-allowed; }
  button:focus-visible, a:focus-visible, summary:focus-visible {
    outline: 4px solid #9e1820;
    outline-offset: 8px;
  }
}
.event-header {
  color: #150000;
  background: white;
}
.header-inner {
  display: flex;
  align-items: center;
  gap: 40px;
  width: 92%;
  max-width: 3000px;
  min-height: 128px;
  margin: 0 auto;
}
.brand {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
}
.brand-logo {
  display: block;
  width: 264px;
  height: auto;
  object-fit: contain;
}
.account-avatar {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  color: #ec3338;
  background: #fff4f4;
  border-radius: 50%;
  font-weight: 700;
}
.event-heading {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 36px;
  min-width: 0;
  padding-left: 40px;
  border-left: 2px solid rgba(255,255,255,.35);
  h1 { font-size: 40px; font-weight: 700; overflow-wrap: anywhere; }
  p { color: #999999; font-size: 30px; }
}
.account-menu { position: relative; flex: 0 0 auto; }
.event-title-row {
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 0;
  h1 { min-width: 0; }
}
.status-badge {
  flex: 0 0 auto;
  padding: 8px 14px;
  color: #a30a1b;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  background: #fff;
  border-radius: 8px;
  &.on_sale { color: #fff; background: #198754; }
}
.account-button {
  display: flex;
  align-items: center;
  gap: 20px;
  min-height: 88px;
  color: inherit;
  font-size: 32px;
  font-weight: 600;
  border: 0;
  background: transparent;
  list-style: none;
  &::-webkit-details-marker { display: none; }
}
.account-name { max-width: 320px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.account-caret {
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 12px solid currentColor;
}
.account-links {
  box-sizing: border-box;
  position: absolute;
  right: 0;
  top: calc(100% + 16px);
  z-index: 30;
  min-width: 400px;
  padding: 16px;
  color: #333;
  background: #fff;
  border: 2px solid #eee;
  border-radius: 20px;
  box-shadow: 0 16px 48px rgba(0,0,0,.12);
  button, a {
    box-sizing: border-box;
    display: block;
    width: 100%;
    padding: 24px;
    text-align: left;
    color: inherit;
    font-size: 30px;
    background: transparent;
    border: 0;
    border-radius: 12px;
    &:hover { background: #fff1f2; color: #ef343c; }
  }
}
.event-content { width: 92%; max-width: 3000px; margin: 0 auto; padding: 58px 0; }
.section-panel {
  margin-bottom: 48px;
  padding: 40px 56px 56px;
  background: #fff;
  border-radius: 40px;
  box-shadow: 0 4px 10px rgba(20,30,50,.04);
  scroll-margin-top: 48px;
}
.video-section {
  padding-top: 36px;
  :deep(.video-shell) { padding-top: 38.3%; background: #1a1a2e; border-radius: 32px; }
  :deep(.video-placeholder) {
    padding: 48px;
    text-align: center;
    > .van-icon { display: none; }
    strong { margin-top: 0; font-size: 76px; font-weight: 700; }
    span { margin-top: 20px; color: #aaa9b6; font-size: 36px; }
  }
}
.product-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 40px; }
.product-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  border: 2px solid #ececec;
  border-radius: 30px;
}
.product-image-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
}
.product-image { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.product-info { display: flex; flex: 1; flex-direction: column; padding: 36px 40px 40px; }
.product-title-row {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  h2 {
    flex: 1;
    min-width: 0;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    max-height: 2.8em;
    overflow: hidden;
    font-size: 36px;
    font-weight: 700;
    line-height: 1.4;
    // Chrome 70 supports prefixed line clamping and break-word, but not anywhere.
    overflow-wrap: break-word;
    word-break: break-word;
  }
}
.details-button {
  flex: 0 0 auto;
  padding: 8px 24px;
  color: #ef343c;
  font-size: 28px;
  font-weight: 600;
  background: #fff1f2;
  border: 0;
  border-radius: 16px;
}
.estimated-price { margin: 12px 0 20px; color: #666; font-size: 32px; }
.product-tags { display: flex; flex-wrap: wrap; align-items: center; gap: 16px; margin-bottom: 32px; }
.reservation-tag, .reservation-count {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 20px;
  font-size: 26px;
  line-height: 1.4;
  border-radius: 8px;
}
.reservation-tag {
  color: #ef343c;
  font-weight: 600;
  background: #fff1f2;
  &.closed { color: #999; background: #f0f0f0; }
}
.reservation-count {
  color: #957039;
  background: #fff7e6;
  .van-icon { color: #eeb448; }
}
.reserve-button, .buy-button {
  min-height: 96px;
  padding: 20px 36px;
  color: #fff;
  font-size: 34px;
  font-weight: 700;
  background: #ef343c;
  border: 0;
  border-radius: 20px;
  &:hover:not(:disabled) { background: #d92730; }
  &:disabled { color: #999; background: #f0f0f0; }
}
.reserve-button { width: 100%; margin-top: auto; }
.reservations-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  margin-bottom: 36px;
  h2 { display: inline-flex; align-items: center; gap: 20px; font-size: 40px; font-weight: 700; }
  p { color: #999; font-size: 26px; }
}
.reservation-total {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  padding: 0 14px;
  color: #fff;
  font-size: 30px;
  background: #ef343c;
  border-radius: 24px;
}
.reservation-list { display: flex; flex-direction: column; gap: 24px; }
.reservation-card {
  display: flex;
  align-items: center;
  gap: 44px;
  min-height: 200px;
  padding: 32px 40px;
  border: 2px solid #ececec;
  border-radius: 28px;
}
.reservation-image {
  flex: 0 0 128px;
  width: 128px;
  height: 128px;
  object-fit: contain;
  background: #fafbfc;
  border: 2px solid #eee;
  border-radius: 24px;
}
.fallback-image { display: flex; align-items: center; justify-content: center; color: #bbb; }
.reservation-info {
  flex: 1;
  min-width: 0;
  h3 { font-size: 36px; font-weight: 700; overflow-wrap: anywhere; }
  p { margin: 4px 0; color: #999; font-size: 30px; }
}
.coupon-claimed { display: inline-flex; align-items: center; gap: 8px; color: #bb650d; font-size: 28px; }
.reservation-actions { display: flex; flex: 0 0 auto; align-items: center; gap: 24px; }
.buy-button {
  min-width: 272px;
  min-height: 88px;
  font-size: 32px;
  &:disabled { min-width: 372px; }
}
.cancel-reservation {
  min-height: 88px;
  padding: 16px 0;
  color: #ef343c;
  font-size: 32px;
  background: transparent;
  border: 0;
}
.page-state, .content-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 28px;
  padding: 72px 48px;
  color: #888;
  text-align: center;
  h1, strong { color: #333; font-size: 44px; font-weight: 600; }
  p, span { font-size: 32px; }
}
.page-state { min-height: 100vh; }
.content-empty { min-height: 440px; }
.state-button {
  min-width: 280px;
  height: 88px;
  font-size: 32px;
  background: #ef343c;
  border-color: #ef343c;
  border-radius: 16px;
}
.text-action { padding: 12px 0; color: #ef343c; font-size: 30px; background: transparent; border: 0; }
@media (max-width: 1100px) {
  .header-inner, .event-content { width: 94%; }
  .header-inner { gap: 28px; }
  .event-heading { flex-direction: column; align-items: flex-start; gap: 4px; padding-left: 28px; }
  .section-panel { padding: 36px; }
  .product-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 28px; }
  .product-info { padding: 28px; }
  .product-title-row { flex-wrap: wrap; gap: 12px; h2 { flex-basis: 100%; } }
  .reservation-card { gap: 28px; }
}
@media (max-width: 800px) {
  .product-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .brand-logo { width: 200px; height: 60px; }
  .event-heading h1 { font-size: 34px; }
  .account-name { max-width: 180px; }
  .reservation-card { flex-wrap: wrap; }
  .reservation-actions { width: 100%; justify-content: flex-end; }
  .video-section :deep(.video-placeholder) { strong { font-size: 52px; } span { font-size: 28px; } }
}
@media (max-width: 520px) {
  .product-grid { grid-template-columns: minmax(0, 1fr); }
  .header-inner { flex-wrap: wrap; padding: 24px 0; }
  .event-heading { flex: 1 1 60%; }
  .account-menu, .sign-in-button { margin-left: auto; }
  .video-section :deep(.video-shell) { padding-top: 56.25%; }
}
</style>
